import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetVatRate } from '@features/vatRates/hooks/useGetVatRate'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useDeepCompareMemoize } from '../../../../../hooks/useDeepCompareMemoize'
import { useInvoicingSettingsHost } from '../../../contexts/invoicingSettingsHostContext'
import { useZervantDocumentTemplateSettings } from '../../../hooks/useZervantDocumentTemplateSettings'
import { useZervantSettings } from '../../../hooks/useZervantSettings'
import { getLogoAndBrandingSavedTemplateSettings } from '../../../modules/logoAndBranding'
import { getInvoicingSettingsPreviewPayload } from '../../../utils/getInvoicingSettingsPreviewPayload'
import { getInvoicingSettingsPreviewValues } from '../utils/getInvoicingSettingsPreviewValues'
import { getUnsavedZervantSettings } from '../utils/getUnsavedZervantSettings'

export const useInvoicingSettingsPreviewPayload = () => {
  const [documentDate] = useState(() => new Date())
  const { t } = useTranslation()
  const { organization, isLoading: isLoadingOrganization } = useUserOrganization()
  const { getVatRate, isLoading: isLoadingVatRate } = useGetVatRate()
  const { data: zervantSettings, isLoading: isLoadingZervantSettings } = useZervantSettings()
  const {
    data: documentTemplateSettings,
    dataUpdatedAt: templateDataUpdatedAt,
    isLoading: isLoadingDocumentTemplateSettings,
  } = useZervantDocumentTemplateSettings()
  const { previewPatch } = useInvoicingSettingsHost()

  const isLoading =
    isLoadingDocumentTemplateSettings || isLoadingOrganization || isLoadingVatRate || isLoadingZervantSettings

  const invoiceDraftPayload = useMemo(() => {
    // A template that never loaded keeps the skeleton. A loaded template may be empty for a new organization.
    if (isLoading || !templateDataUpdatedAt || !zervantSettings) {
      return undefined
    }

    const unsavedZervantSettings = getUnsavedZervantSettings(zervantSettings, previewPatch)
    const savedTemplateSettings = getLogoAndBrandingSavedTemplateSettings(documentTemplateSettings)

    const customTemplateSettings = {
      ...savedTemplateSettings,
      ...previewPatch?.customTemplateSettings,
      color: {
        ...savedTemplateSettings.color,
        ...previewPatch?.customTemplateSettings?.color,
      },
    }

    const { previewValues, visibleColumns } = getInvoicingSettingsPreviewValues({
      isVatExempted: organization?.isVatExempted,
      t,
      vatRate: getVatRate({ vatRateId: unsavedZervantSettings.values.company.vatRateId }),
      zervantSettings: unsavedZervantSettings,
    })

    return getInvoicingSettingsPreviewPayload({
      customTemplateSettings,
      documentDate,
      organization,
      previewValues,
      t,
      visibleColumns,
      zervantSettings: unsavedZervantSettings,
    })
  }, [
    documentDate,
    documentTemplateSettings,
    getVatRate,
    isLoading,
    organization,
    previewPatch,
    t,
    templateDataUpdatedAt,
    zervantSettings,
  ])

  // Save replaces the settings cache, refetches the template and then clears the patch. Each step builds
  // a new payload with the same content, and useInvoicePreview requests on every new reference.
  const stableInvoiceDraftPayload = useDeepCompareMemoize(invoiceDraftPayload)

  return { invoiceDraftPayload: stableInvoiceDraftPayload, isLoading }
}
